'use client';

import { SetStateAction, useState } from 'react';

interface Prefecture {
  id: number;
  name: string;
}

interface AreaWithPrefectures {
  id: number;
  name: string;
  prefectures: Prefecture[];
}

interface Props {
  prefectures: Prefecture[];
  areasWithPrefectures: AreaWithPrefectures[];
  selectedPrefectureIds: number[];
  errorMessage?: string;
  isMobile: boolean;
  setSelectedPrefectureIds: (PrefectureIds: SetStateAction<number[]>) => void;
}

export default function PrefectureMultiModalJob({
  prefectures,
  areasWithPrefectures,
  selectedPrefectureIds,
  errorMessage,
  isMobile,
  setSelectedPrefectureIds,
}: Props) {
  const [isModalActive, setIsModalActive] = useState(false);
  // 全国のID
  const nationalAreaId = 9;

  const selectedPrefectureNames =
    selectedPrefectureIds.length === 0
      ? '都道府県を選択'
      : prefectures
          .filter((p) => selectedPrefectureIds.includes(p.id))
          .map((p) => p.name)
          .join('、');

  const allChildSelected = (area: AreaWithPrefectures): boolean => {
    if (!area.prefectures?.length) return false;
    return area.prefectures.every((p) => selectedPrefectureIds.includes(p.id));
  };

  const toggleAll = (area: AreaWithPrefectures) => {
    const childIds = area.prefectures.map((p) => p.id);
    if (allChildSelected(area)) {
      setSelectedPrefectureIds((prev) =>
        prev.filter((id) => !childIds.includes(id)),
      );
    } else {
      setSelectedPrefectureIds((prev) =>
        Array.from(new Set([...prev, ...childIds])),
      );
    }
  };

  const togglePrefecture = (id: number) => {
    setSelectedPrefectureIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  return (
    <div className={!isMobile ? 'u-wp90' : ''}>
      <div
        className="p-prefecture-modal-v2__selector u-bg-white"
        onClick={() => setIsModalActive(true)}
      >
        <span>{selectedPrefectureNames}</span>
      </div>

      <div className="p-entry-form__input__error u-h-1em u-mt8">
        <p>{errorMessage}</p>
      </div>

      {isModalActive && (
        <>
          <div className="p-multiple-workplace-modal">
            <div className="p-multiple-workplace-modal__overlay js-modal-close" />
            <div className="p-multiple-workplace-modal__inner">
              <div className="p-multiple-workplace-modal__header">
                <img
                  className="p-multiple-workplace-modal__icon"
                  width="20"
                  height="20"
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEgAAABaCAMAAAAsLeDlAAAAnFBMVEUAAAAQmlUfqVkRmVURmlUWn1sRmVUQmlUQmlUZnlkRmlUSm1YSnlcQmVQRmVUQmlUQmlUUnFgQmlURmlUQmlUQmlQRmlUSmlYQmlURmlUQmlYQm1YSnFcQmVQRmlURm1QQmlURmlURmlURmlUTm1YRnFcWnVgXoF8TnFYQmlQRmlURmlUQmlURm1YSm1YRmlUQmlUUnVgSmlQQmVTh9Q1RAAAAM3RSTlMA+wj38w7myOoUkDgc3NbOXhi5ppx+eFOtWU88Ke7Cb6GVgmVBLiEKNOC0h71LRphrJXIW5eP7AAAEMUlEQVRYw6VYaYOiMAxNAQUExVs8kfEcb4f//99207hbLNAp9X1iaqcmLy8vFVCgcUvSS6fpMMdru+vxpAcmOJ5dlkno3K8W1EJv3MnK4aVH0Mb+zjIF+lvQgj/8f4wXn7vz3d4PF8FkvBZBrnSieiz/7U4WcsLTgU2fsbQBaoSrVyyjfXkhNxFtaKrzuy5pV3JQ7HEpqJHinDFtOR9Aia6XIU6V21oZIlrAb2isM4TrQyno0/gAGvhhXKB+dTwj0MONsxmVFG+EHzgT0MWTy+pSaJkZLttH0EeDCyGVu4JHOoE62Hsl/+NW8BOcY/fb9qJTOiv2/ZwhG2/KTfCcAUhY3JuZgB1fC4LiPZxvIgdVYUmdNSx4wCqQKy0lN0A9SzpM7KwEa+nbsHTN/2sL3PIFeVjDjNBuJZP5bPPlZoQofKcJ15J8QN/WW2lXREtrJzJ9EGHNo9QOIqQ9cvGAPPqUR/ge5djhDvO2+sR/3tDzFx0qc2hPQcauzbN768YBrtDj99/HcaGqXlBmw25BJ0dc4YXa4lOYJwgVy+blhs5jmsuFO//L41Jo3w2UY8ElJ7thBx8ipFpWZx+qkMgNtseFHoBPOQr8YGI7qIL1LX8PLkzJP5r59ZVEZ1mDsbyhoXZTSjHO04nCmKl8iPEIBDZIMp13lr7QVvp2Xwp5jjlRJl2pZn218Ut16yHLDUBhbEHg/vfvIagwwdbML6BP7AFbMd+HJ3ICBQJkO7+AAt4BquYpmW6inh+UikCbi52JBjGNqMPZWWJcH3FE7FBcplUTZD95q80+0tEBOfPhInRkqOwQD7K4w40Me02Qv6ReO8En3f+gXrtRpxj7EQ2S1suPeuYOSTLqvlRwBXPPPrCXEmOKzHCKEDeOhb5EQjWca2RoMdkJhWY2aan3SYgutanR7KexyPz8YDK4jSBSvDeJWw2apMn9CKxlzr9WL7rq39ioVHYjZzCMQta/Qwo1CvuymhLd0q2WFW+1guo3y//C/m2AAU7vDRMypVGr7TvrSkbt+FAbFxRIfiG0aXDXxI0CkmVlh1ATUVHKPZu0pA/SELl3wc2uUAf+UnSHgNVG3iyogWG5uV8zfQmIXw9phbZYALo4YApemYr3DtZAO7mUmK4sQquOhGLFUM9uehVrYmJVvRBiPZdasuyrf0tP8WNXTZNQ3Vqx4a5H05ZhXVR3HyuiNlSCXhI4i9/32Go1NTqVlZdfbTTD31wxS/V4jBS+2xIvV9QYqDeO+djUcVOLbhyKix8SrYNeu1oEVyw824Ienl5WYeFbmwqmi4DvHxfXHVz/qTMe6KTiObWHzYwVT5o7xF09TJn8jmvrZHRhMDqpJWK0zc6hk4RXbJjZOYKnfoMah+Izw42z0pmFQZzRsDJF4GUC7AHmeLbFzW8Cn8CPM0J0hA8RDCKnferCb/gDLa4HzyP5ThEAAAAASUVORK5CYII="
                  alt="icon"
                />
                <div className="p-multiple-workplace-modal__title">
                  希望勤務地（複数可）
                </div>
              </div>

              <div className="p-multiple-workplace-modal__body">
                {areasWithPrefectures.map((area, i) => (
                  <div
                    className="p-multiple-workplace-modal__container"
                    key={`area-${i}`}
                  >
                    <div className="p-multiple-workplace-modal__item">
                      <div className="p-multiple-workplace-modal__parent">
                        {area.id === nationalAreaId ? (
                          <label className="p-multiple-workplace__label">
                            <input
                              type="checkbox"
                              className="p-multiple-workplace__checkbox"
                              value={area.prefectures[0]?.id}
                              checked={selectedPrefectureIds.includes(
                                area.prefectures[0]?.id,
                              )}
                              onChange={() =>
                                togglePrefecture(area.prefectures[0]?.id)
                              }
                            />
                            {area.name}
                          </label>
                        ) : (
                          <label className="p-multiple-workplace__label">
                            <input
                              type="checkbox"
                              className="p-multiple-workplace__checkbox"
                              checked={allChildSelected(area)}
                              onChange={() => toggleAll(area)}
                            />
                            {area.name}
                          </label>
                        )}
                      </div>

                      {area.prefectures?.length &&
                        area.id !== nationalAreaId && (
                          <div className="p-multiple-workplace-modal__child">
                            {area.prefectures.map((pref, j) => (
                              <label
                                className="p-multiple-workplace__label"
                                key={`prefecture-${j}`}
                                htmlFor={`prefecture${pref.id}`}
                              >
                                <input
                                  id={`prefecture${pref.id}`}
                                  type="checkbox"
                                  className="p-multiple-workplace__checkbox"
                                  value={pref.id}
                                  checked={selectedPrefectureIds.includes(
                                    pref.id,
                                  )}
                                  onChange={() => togglePrefecture(pref.id)}
                                />
                                {pref.name}
                              </label>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-multiple-workplace-modal__footer">
                <button
                  type="button"
                  className="p-multiple-workplace__button -border-green"
                  onClick={() => setIsModalActive(false)}
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
          <div
            className="v-prefecture-modal__background"
            onClick={() => setIsModalActive(false)}
          />
        </>
      )}
    </div>
  );
}
